import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, number, extend, useDataModel, arrayOf
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { Drawer, DrawerHeader, Button } from '@one-thd/sui-atomic-components';
import { AddToCoreContext, AddToCoreProvider } from './AddToCoreContext';
import { SCREENS } from './coreConstants';
import { triggerAnalytics } from './coreHelpers';
import { TRACK, OPERATION_ADD_ITEMS } from '../AddToConstants';
import { useListDetails, useUpdateList, listDetailsModel, updateListModel } from '../hooks/useLists';
import { useStorefrontData } from '../hooks/useStorefrontData';
import { SignIn } from '../common/addto-signin/SignIn';
import { CreateList } from '../addto/list/CreateList';
import { SelectList } from '../addto/list/SelectList';
import { SelectListGroup } from '../addto/list/SelectListGroup';
import { AddToListSuccess } from '../addto/list/AddToListSuccess';
import { AddToLoading } from '../common/addto-loading/AddToloading';

// Screen title mapping (core screens only)
const SCREEN_TITLES = {
  [SCREENS.LIST_CREATE_LIST]: 'Create List',
  [SCREENS.LIST_SELECT_LIST]: 'Select List',
  [SCREENS.LIST_SELECT_GROUP]: 'Select Group',
  [SCREENS.LIST_SUCCESS]: 'Added to List',
};

export const AddToListContent = ({
  products,
  skipListId,
  isSharedList,
  listId,
  onMaterialsAdded,
}) => {
  const {
    storeId,
    screen,
    pushScreen,
    replaceScreen,
    handleClose,
    selectedProductDetails,
    setSelectedListId,
    setSelectedListName,
  } = useContext(AddToCoreContext);

  const {
    svocID, userID, customerType, isLoggedIn
  } = useThdCustomer() || {};
  const { storeFrontSVOCID, storeFrontUserID } = useStorefrontData();

  const [favoriteCount, setFavoriteCount] = useState(0);
  const [newListDetails, setNewListDetails] = useState({ listName: '', listId: '' });
  const [selectedList, setSelectedList] = useState({ listId: null, listName: null });

  const {
    LIST_CREATE_LIST, LIST_SELECT_LIST, LIST_SELECT_GROUP, SIGN_IN, LIST_SUCCESS, LOADING
  } = SCREENS;

  const itemId = selectedProductDetails?.itemId
    || (products && products[0] && products[0].itemId);

  const { data } = useDataModel('product', {
    variables: { itemId, storeId },
    skip: !itemId || products?.length > 1
  });

  const productData = data?.product || {};
  const productValue = productData?.pricing?.value;
  const identifiers = productData?.identifiers;
  const primaryImage = productData?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  const isItemBundle = productData?.bundleFlag;
  const bundleItems = productData?.bundleItems || [];
  const bundledProducts = bundleItems.map((item) => ({
    itemId: item.id || item.defaultProductId,
    quantity: item.quantity || selectedProductDetails?.quantity || 1
  }));

  const canSkipListDetails = useMemo(() => {
    if (products?.length === 0) return true;
    if (storeFrontSVOCID) {
      return !storeFrontSVOCID || !storeFrontUserID;
    }
    return !svocID || !userID;
  }, [products?.length, storeFrontSVOCID, storeFrontUserID, svocID, userID]);

  const { listData, listLoading, listRefetch } = useListDetails({
    customerAccountID: storeFrontSVOCID || svocID,
    userId: storeFrontSVOCID ? storeFrontUserID : userID,
    customerType: storeFrontSVOCID ? null : customerType?.toUpperCase(),
    skip: canSkipListDetails
  });

  const { listDetails: listDetailsData = null } = listData?.listDetails?.listResponse || {};
  const listDetails = (listDetailsData || []).filter((list) => list.listId !== skipListId);
  const listsExist = !!listDetails?.length;

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS
  });

  const onCreateList = () => {
    listRefetch();
  };

  const isSelectionParentScreen = screen === SCREENS.ADD_TO_SELECTION;
  const isEntryLoadingScreen = screen === LOADING;

  // Determine initial screen
  useEffect(() => {
    if (screen && !isSelectionParentScreen && !isEntryLoadingScreen) return;

    let nextScreen = null;
    if (!isLoggedIn) {
      nextScreen = SIGN_IN;
    } else if (listLoading) {
      nextScreen = LOADING;
    } else if (listsExist) {
      nextScreen = LIST_SELECT_LIST;
    } else {
      nextScreen = LIST_CREATE_LIST;
    }

    if (isEntryLoadingScreen && nextScreen !== LOADING) {
      replaceScreen(nextScreen);
    } else if (screen !== nextScreen) {
      pushScreen(nextScreen);
    }
  }, [
    isEntryLoadingScreen,
    isLoggedIn,
    isSelectionParentScreen,
    listLoading,
    listsExist,
    pushScreen,
    replaceScreen,
    screen,
  ]);

  // Handle update list response
  useEffect(() => {
    if (updateListResponse?.data?.updateList?.id) {
      const updatedListId = updateListResponse.data.updateList.id;
      const listName = selectedList?.listName || newListDetails?.listName;
      if (updatedListId && listName) {
        setSelectedListId(updatedListId);
        setSelectedListName(listName);
      }
      if (onMaterialsAdded) {
        onMaterialsAdded();
      }
      pushScreen(LIST_SUCCESS);
    }
  }, [updateListResponse?.data]);

  const listIdToPass = selectedList?.listId || newListDetails?.listId;

  return (
    <>
      {screen === SIGN_IN && <SignIn onClose={handleClose} />}
      {screen === LOADING && <AddToLoading />}
      {screen === LIST_CREATE_LIST && (
        <CreateList
          onClose={handleClose}
          onCreate={onCreateList}
          products={products}
          isExistingLists={listsExist}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
          isItemBundle={isItemBundle}
          bundledProducts={bundledProducts}
        />
      )}
      {screen === LIST_SELECT_LIST && (
        <SelectList
          products={products}
          listDetails={listDetails}
          onClose={handleClose}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
          onCreate={onCreateList}
          isExistingLists={listsExist}
          selectedList={selectedList}
          setSelectedList={setSelectedList}
        />
      )}
      {screen === LIST_SELECT_GROUP && (
        <SelectListGroup
          listId={listIdToPass || listId}
          storeId={storeId}
          isSharedList={isSharedList}
          products={selectedProductDetails?.itemId ? [{
            itemId: selectedProductDetails.itemId,
            quantity: selectedProductDetails.quantity,
            price: selectedProductDetails?.productData?.product?.pricing?.value
          }] : products}
          identifiers={identifiers}
          primaryImage={primaryImage}
          handleClose={handleClose}
          isNewlyCreated={!!newListDetails?.listId && !selectedList?.listId}
          newListName={newListDetails?.listName}
          isItemBundle={isItemBundle}
          bundledProducts={bundledProducts}
        />
      )}
      {(screen === LIST_SUCCESS || screen === 'DEFAULT') && (
        <AddToListSuccess
          newListDetails={newListDetails}
          onClose={handleClose}
          products={products}
        />
      )}
    </>
  );
};

AddToListContent.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
  })),
  skipListId: PropTypes.bool,
  isSharedList: PropTypes.bool,
  listId: PropTypes.string,
  onMaterialsAdded: PropTypes.func,
};

AddToListContent.defaultProps = {
  products: [],
  skipListId: false,
  isSharedList: false,
  listId: '',
  onMaterialsAdded: null,
};

const AddToListDrawerHeader = ({ onClose }) => {
  const { screen, popScreen, canGoBack } = useContext(AddToCoreContext);

  const showBack = canGoBack && screen !== SCREENS.LIST_SUCCESS;

  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    popScreen();
  };

  return (
    <DrawerHeader
      data-testid="core-add-to-list-header"
      title={SCREEN_TITLES[screen] || 'Add to List'}
      onClose={onClose}
      onBack={showBack ? handleBackPress : null}
    />
  );
};

AddToListDrawerHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export const AddToList = (props) => {
  const {
    products,
    skipListId,
    isSharedList,
    listId,
    onMaterialsAdded,
    renderButton,
    defaultOpen,
    storeId: storeIdProp
  } = props;

  const [open, setOpen] = useState(defaultOpen || false);
  const [selectedProductDetails, setSelectedProductDetails] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const store = useStore();
  const storeId = storeIdProp || store?.storeId;

  const handleOpen = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const coreValue = {
    addToType: 'List',
    products,
    storeId,
    onClose: handleClose,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    selectedGroup,
    setSelectedGroup,
    listRefetch: () => {},
    projectRefetch: () => {},
  };

  const renderTriggerButton = () => {
    if (renderButton) {
      return renderButton({ onClick: handleOpen });
    }
    return (
      <Button
        fullWidth
        variant="secondary"
        onClick={handleOpen}
        disabled={!products?.length}
        aria-label="Add to List"
        data-testid="core-add-to-list-button"
      >
        Add to List
      </Button>
    );
  };

  return (
    <>
      {renderTriggerButton()}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <Drawer open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <AddToCoreProvider value={coreValue}>
          <AddToListDrawerHeader onClose={handleClose} />
          <AddToListContent
            products={products}
            skipListId={skipListId}
            isSharedList={isSharedList}
            listId={listId}
            onMaterialsAdded={onMaterialsAdded}
          />
        </AddToCoreProvider>
      </Drawer>
    </>
  );
};

AddToList.displayName = 'AddToList';

AddToList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number,
  })),
  skipListId: PropTypes.bool,
  isSharedList: PropTypes.bool,
  listId: PropTypes.string,
  onMaterialsAdded: PropTypes.func,
  renderButton: PropTypes.func,
  defaultOpen: PropTypes.bool,
  storeId: PropTypes.string,
};

AddToList.defaultProps = {
  products: [],
  skipListId: false,
  isSharedList: false,
  listId: '',
  onMaterialsAdded: null,
  renderButton: null,
  defaultOpen: false,
  storeId: null,
};

AddToList.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  },
  listDetailsModel,
  updateListModel,
  CreateList,
  SelectListGroup,
  AddToListSuccess
);
